import {
    getOrganizationCompareInCharts,
    IClauseChecklist,
    ICompareInChartMinimal,
    IFilter,
    IOrganization,
} from "@/api";
import { ClauseChecklistChip } from "@/components/shared/chips/ClauseChecklistChip";
import { DateFilterInput } from "@/components/shared/table/DateFilterInput";

import { Table } from "@/components/shared/table/Table";
import { formatDate } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ViewAllCompareInChartsButton } from "../buttons/ViewAllCompareInChartsButton";
import { ComparisonLink } from "./ComparisonLink";

const columns: GridColDef<ICompareInChartMinimal>[] = [
    {
        field: "name",
        headerName: "Name",
        display: "flex",
        renderCell: (params) => {
            return <ComparisonLink comparison={params.row} />;
        },
    },
    {
        field: "clause_checklist",
        headerName: "Clause Checklist",
        display: "flex",
        valueGetter: (value: IClauseChecklist) => {
            return value.name;
        },
        renderCell: (params) => {
            return (
                <ClauseChecklistChip
                    clauseChecklist={params.row.clause_checklist.name}
                />
            );
        },
    },
    {
        field: "created_at",
        headerName: "Date Uploaded",
        display: "flex",
        type: "date",
        filterOperators: [
            {
                label: "Date is",
                value: "dateIs",
                getApplyFilterFn: (filterItem) => {
                    if (!filterItem.value) {
                        return null;
                    }
                    return ({ value }) => {
                        return value === filterItem.value;
                    };
                },
                InputComponent: DateFilterInput,
            },
        ],
        valueFormatter: (value) => {
            return formatDate(value, "PPp");
        },
    },
];

interface IProps {
    /**
     * the organization to display the recent CompareInCharts for
     */
    organization: IOrganization;
}

/**
 * a table that displays the recent CompareInCharts for an organization
 */
export const RecentCompareInChartsTable = ({ organization }: IProps) => {
    const autoSizeOptions = {
        columns: ["name", "clause_checklist", "created_at"],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const [rows, setRows] = useState<ICompareInChartMinimal[]>([]);

    const filter: IFilter = {
        sort_by: "created_at",
        order: "desc",
    };

    const page = 1;
    const size = 5;

    // Fetch 5 most recent scopes
    const { data, isPending } = useQuery({
        queryKey: [
            "admin-organization-details-recent-compare-in-charts",
            organization.id,
        ],
        queryFn: () =>
            getOrganizationCompareInCharts(organization.id, page, size, filter),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);
    return (
        <Box sx={{ mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" mx={2}>
                <Typography variant="h6">Recent Comparisons</Typography>
                <ViewAllCompareInChartsButton organization={organization} />
            </Stack>
            <Box sx={{ mt: 2, mx: 1 }}>
                <Table
                    loading={isPending}
                    rows={rows}
                    columns={columns}
                    autoSize
                    autosizeOptions={autoSizeOptions}
                    hideFooter
                />
            </Box>
        </Box>
    );
};
